import React, { useState } from 'react';
import styled from 'styled-components';
import { compose } from 'ramda';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { Grid } from '_utils/components/Corporate';
import { imagePlaceholderSrc } from '_utils/styles';
import { useTranslation } from 'react-i18next';
import {
  PreviewContainer,
  ImageContainer,
  Image,
  ExpandButton,
  ExpandableIndicator,
  ExpandableWrapper,
  ShowMore,
  ShowMoreCount,
  ImageListContainer,
  ImageListBlock,
  ImageCell,
  CaptionCell,
  ImageListGridY,
  ExpEdImage,
  CaptionWrap
} from './StyledGallery';
import { withModalContext } from '_containers/ModalContext';
import ModalGallery from './ModalGallery';
import Expandable from '_utils/icons/Expandable';
import { convertSrcJss, createSrcSet } from '_utils/media';
import ContentRichText from '../ContentRichText';

import { withBannerCurve } from '_utils/components/Corporate/BannerCurve';
import { BackgroundColors } from '_utils/components/Corporate/Section';
import PropTypes from 'prop-types';

const EditorNotice = compose(withEditModeNotice)(styled.div``);

const MODAL_ID = 'gallery';

const getValidGalleryItems = (gallery) =>
  gallery.filter(({ item }) => Boolean(item?.data?.value?.src));

const Gallery = ({ fields, params, editMode, setModal }) => {
  const [t] = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const gallery = fields?.data?.datasource?.gallery || [];

  // filter by items that have data
  let galleryImages = getValidGalleryItems(gallery).map(({ item }) => item.data);

  const previewImages = galleryImages.slice(0, 3);

  let imageClassModifier = null;

  if (previewImages.length === 2) {
    imageClassModifier = 'double';
  }

  if (previewImages.length === 3) {
    imageClassModifier = 'triple';
  }

  const showMoreCount = galleryImages.length - 3;

  /* DEV auto expand */
  // useEffect(() => {
  //   setExpanded(true);
  //   setModal({
  //     id: MODAL_ID,
  //     content: <ModalGallery gallery={gallery} />,
  //     onClose: () => setModal(null)
  //   });
  // }, []);

  let editorNotice = null;

  if (editMode && !fields?.data?.datasource?.gallery) {
    editorNotice = 'Gallery has no data source';
  }

  if (editMode && fields?.data?.datasource?.gallery && !fields.data.datasource.gallery.length) {
    editorNotice = 'Gallery contains no items';
  }

  if (!galleryImages.length && !editMode) {
    return null;
  }

  return (
    <Grid.Container>
      {editMode && editorNotice && (
        <EditorNotice noticeEnabled={true} noticeMessage={editorNotice} />
      )}
      {editMode && !editorNotice && (
        <ImageListContainer>
          {editMode &&
            !editorNotice &&
            gallery.map(({ item, caption, credit, title }, index) => (
              <ImageListBlock key={`image-list-block-${index}`}>
                <ImageListGridY>
                  <ImageCell>
                    <ExpEdImage field={item?.data} />
                  </ImageCell>
                  <CaptionCell>
                    <CaptionWrap>
                      <p>
                        <Text tag="span" field={title?.data} />
                      </p>
                      <p>
                        <ContentRichText tag="span" field={caption?.data} />
                      </p>
                      <p>
                        <Text tag="span" field={credit?.data} />
                      </p>
                    </CaptionWrap>
                  </CaptionCell>
                </ImageListGridY>
              </ImageListBlock>
            ))}
        </ImageListContainer>
      )}

      {!editMode && previewImages && (
        <PreviewContainer role="group" aria-label="gallery" className={imageClassModifier}>
          {previewImages &&
            previewImages.map(({ value, editable }, index) => (
              <ImageContainer key={`gallery-preview-${index}`} className={imageClassModifier}>
                <Image
                  className="lazyload"
                  draggable={false}
                  field={{
                    value: {
                      src: imagePlaceholderSrc,
                      alt: value.alt,
                      'data-src': convertSrcJss(value.src) + '?mw=640',
                      'data-srcset': createSrcSet(value.src, [640, 1200])
                    },
                    editable: editable
                  }}
                />
                {showMoreCount > 0 && index === 2 && (
                  <ShowMore>
                    <ShowMoreCount>+{showMoreCount}</ShowMoreCount>
                  </ShowMore>
                )}
              </ImageContainer>
            ))}

          <ExpandButton
            aria-label={t('gallery-accessible-open-label')}
            aria-haspopup={true}
            aria-expanded={expanded}
            onClick={() => {
              setExpanded(true);
              setModal({
                id: MODAL_ID,
                content: <ModalGallery gallery={gallery} />,
                onClose: () => setModal(null)
              });
            }}
          >
            <ExpandableIndicator aria-hidden={true}>
              <ExpandableWrapper>
                <Expandable />
              </ExpandableWrapper>
            </ExpandableIndicator>
          </ExpandButton>
        </PreviewContainer>
      )}
    </Grid.Container>
  );
};

Gallery.propTypes = {
  fields: PropTypes.shape({
    data: PropTypes.shape({
      datasource: PropTypes.shape({
        gallery: PropTypes.arrayOf(
          PropTypes.shape({
            item: PropTypes.shape({
              data: PropTypes.shape({
                value: PropTypes.shape({
                  src: PropTypes.string,
                  alt: PropTypes.string,
                }),
              }),
            }),
            caption: PropTypes.shape({
              data: PropTypes.shape({
                value: PropTypes.string,
              }),
            }),
            credit: PropTypes.shape({
              data: PropTypes.shape({
                value: PropTypes.string,
              }),
            }),
            title: PropTypes.shape({
              data: PropTypes.shape({
                value: PropTypes.string,
              }),
            }),
          })
        ),
      }),
    }),
  }),
  params: PropTypes.object,
  editMode: PropTypes.bool,
  setModal: PropTypes.func,
};

export default compose(
  withEditMode,
  withModalContext,
  withBannerCurve({
    backgroundColor: BackgroundColors.Grey,
    renderNothing: ({ fields }) => {
      const hasNoImages = getValidGalleryItems(fields?.data?.datasource?.gallery || []).length === 0;
      return hasNoImages;
    },
    renderCurve: ({ fields, params }) => {
      //null means not rendering blue curved background
      const hasSomeImages = getValidGalleryItems(fields?.data?.datasource?.gallery || []).length > 0;
      const showCurvedBorderOptionSelected = params?.showCurvedBorder === "1";
      return (showCurvedBorderOptionSelected && hasSomeImages) ? true : null;
    }
  })
)(Gallery);
